import { useCallback } from 'react'
import { ReportItem, Report, an, CASStatus } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import { useAnyTrue } from 'hooks/useAnyTrue'
import {
  useAsphaltReportOptionsQuery,
  useInspectionReportOptionsQuery,
  usePunchListReportOptionsQuery,
  useCasReportOptionsQuery,
  useConcretePlacementReportOptionsQuery,
  useConcreteTestReportOptionsQuery,
  useConcreteTestSetReportOptionsQuery,
  useControlPointReportOptionsQuery,
  useLotReportOptionsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from '../../../hooks/useOptions'
import {
  ReportRecordSelectContext,
  ReportRecordSelectProviderProps,
} from './types'
import { join, split, toLabeledValues } from './utils'

const { Provider, useContextFn } =
  createStrictContext<ReportRecordSelectContext>('ReportRecordSelectContext')

export const useReportRecordSelectContext = useContextFn

const ReportRecordSelectProvider: React.FC<ReportRecordSelectProviderProps> = ({
  reports,
  children,
}) => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const defaults = {
    ...DefaultQueryHookOptions,
    variables: { globalId: globalId! },
    skip: !globalId,
  }
  const withClassType = useCallback(
    (ct: ReportItem['classType']) =>
      reports ? reports.some((report) => report.classType === ct) : true,
    [reports]
  )
  const asphalts = useAsphaltReportOptionsQuery({
    ...defaults,
    skip: !withClassType('AsphaltLot'),
  })
  const inspections = useInspectionReportOptionsQuery({
    ...defaults,
    skip: !withClassType('DailyInspectionInspection'),
  })
  const punchLists = usePunchListReportOptionsQuery({
    ...defaults,
    skip: !withClassType('DailyInspectionPunchList'),
  })
  const concreteTests = useConcreteTestReportOptionsQuery({
    ...defaults,
    skip: !withClassType('ConcreteTest'),
  })
  const concretePlacements = useConcretePlacementReportOptionsQuery({
    ...defaults,
    skip: !withClassType('ConcretePlacement'),
  })
  const concreteTestSets = useConcreteTestSetReportOptionsQuery({
    ...defaults,
    skip: !withClassType('ConcreteTestSet'),
  })
  const controlPoints = useControlPointReportOptionsQuery({
    ...defaults,
    skip: !withClassType('DailyInspectionControlPoint'),
  })
  const lots = useLotReportOptionsQuery({
    ...defaults,
    skip: !withClassType('Lot'),
  })
  const caSubmittals = useCasReportOptionsQuery({
    ...DefaultQueryHookOptions,
    skip: !withClassType('ConstructionAdminSubmittal'),
    variables: {
      input: {
        projectId: { equals: globalId },
        statusId: { equals: CASStatus.Closed.id },
        deleted: false,
      },
    },
  })

  const asphaltOptions = useOptions({
    items: asphalts.data?.asphaltLots?.records,
    label: 'asphaltLotId',
    value: join,
    tooltip: () => Report.AsphaltLot.classTypeName,
    sort: 'asphaltLotId',
  })
  const inspectionOptions = useOptions({
    items: inspections.data?.dailyInspectionInspections?.records,
    label: 'inspectionId',
    value: join,
    tooltip: () => Report.Inspection.classTypeName,
    sort: 'inspectionId',
  })
  const punchListOptions = useOptions({
    items: punchLists.data?.dailyInspectionPunchLists?.records,
    label: 'punchListId',
    value: join,
    tooltip: () => Report.PunchList.classTypeName,
    sort: 'punchListId',
  })
  const concreteTestOptions = useOptions({
    items: concreteTests.data?.concreteTests?.records,
    label: 'fieldNumber',
    value: join,
    tooltip: () => Report.ConcreteCylinderIASheet.classTypeName,
    sort: 'fieldNumber',
  })
  const concretePlacementOptions = useOptions({
    items: concretePlacements.data?.concretePlacements?.records,
    label: 'placementId',
    value: join,
    tooltip: () => Report.ConcretePlacementCoreSheet.classTypeName,
    sort: 'placementId',
  })
  const concreteTestSetOptions = useOptions({
    items: concreteTestSets.data?.concreteTestSets?.records,
    label: 'testSetId',
    value: join,
    tooltip: () => Report.ConcreteTestSet.classTypeName,
    sort: 'testSetId',
  })
  const controlPointOptions = useOptions({
    items: controlPoints.data?.dailyInspectionControlPoints?.records,
    label: 'controlPointId',
    value: join,
    tooltip: () => Report.ControlPoint.classTypeName,
    sort: 'controlPointId',
  })
  const lotOptions = useOptions({
    items: lots.data?.lots?.records,
    label: 'lotId',
    value: join,
    tooltip: () => Report.DensityLot.classTypeName,
    sort: 'lotId',
  })
  const casOptions = useOptions({
    items: caSubmittals.data?.constructionAdminSubmittals?.records.filter(
      ({ packetSharePointResourceId }) => packetSharePointResourceId
    ),
    label: 'packageNumber',
    value: join,
    tooltip: () => Report.ConstructionAdminSubmittal.classTypeName,
    sort: 'packageNumber',
  })

  const options = useCallback<ReportRecordSelectContext['options']>(
    (classType) => {
      if (!classType) return []
      let options
      if (classType === 'AsphaltLot') options = asphaltOptions
      else if (classType === 'ConcretePlacement')
        options = concretePlacementOptions
      else if (classType === 'ConcreteTest') options = concreteTestOptions
      else if (classType === 'ConcreteTestSet') options = concreteTestSetOptions
      else if (classType === 'DailyInspectionControlPoint')
        options = controlPointOptions
      else if (classType === 'DailyInspectionInspection')
        options = inspectionOptions
      else if (classType === 'DailyInspectionPunchList')
        options = punchListOptions
      else if (classType === 'Lot') options = lotOptions
      else if (classType === 'ConstructionAdminSubmittal') options = casOptions
      return options ?? []
    },
    [
      asphaltOptions,
      concretePlacementOptions,
      concreteTestOptions,
      concreteTestSetOptions,
      controlPointOptions,
      inspectionOptions,
      lotOptions,
      punchListOptions,
      casOptions,
    ]
  )

  const [loading] = useAnyTrue([
    asphalts.loading,
    inspections.loading,
    punchLists.loading,
    concreteTests.loading,
    concretePlacements.loading,
    concreteTestSets.loading,
    controlPoints.loading,
    lots.loading,
    caSubmittals.loading,
  ])

  const allOptions = useOptions({
    items: Report.clientReports.reduce(
      (types, { classType, classTypeName }) => {
        if (types.some((type) => type.classType === classType)) return types
        if (
          reports &&
          reports.every((report) => report.classType !== classType)
        )
          return types
        types.push({ classType, classTypeName })
        return types
      },
      Array<Pick<ReportItem, 'classType' | 'classTypeName'>>()
    ),
    type: 'custom',
    custom: ({ classType, classTypeName }) => ({
      value: classType,
      label: classTypeName,
      options: options(classType),
    }),
    sort: 'classTypeName',
  })

  return (
    <Provider
      value={{
        fromLabeledValue: (value) => {
          if (!value) return undefined
          return split(value.value)
        },
        fromLabeledValues: (values) =>
          (values ?? []).map(({ value }) => split(value)),
        toLabeledValue: (record) =>
          record ? toLabeledValues([record])[0] : undefined,
        toLabeledValues: (records) => toLabeledValues(records),
        allOptions,
        loading,
        options,
        placeholder: (report) => {
          if (!report) return undefined
          return `Select ${an(report.classTypeName)}`
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default ReportRecordSelectProvider
